import { MUIList } from "@abb/arcadia-ui-react"
import { Dispatch, SetStateAction, useCallback, useMemo } from "react"
import { Guid } from "../models/CustomType"

interface DiMaCheckboxListProps {
  readonly items: DiMaListItem[]
  readonly checkedItemsIds: Guid[]
  readonly setCheckedItemsIds: Dispatch<SetStateAction<Guid[]>>
  readonly options?: {
    readonly sortItemsByTitle?: boolean
    readonly sortCheckedItemsIds?: boolean
  }
}

export interface DiMaListItem {
  id: Guid
  title: string
  description?: string
}

export default function DiMaCheckboxList({
  items,
  checkedItemsIds,
  setCheckedItemsIds,
  options,
}: DiMaCheckboxListProps) {
  const localItems = useMemo(() => {
    if (options?.sortItemsByTitle) {
      return [...items].sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { numeric: true })
      )
    }
    return items
  }, [items, options?.sortItemsByTitle])

  const selectedIndexes = useMemo(
    () =>
      localItems.reduce<number[]>((acc, item, index) => {
        if (checkedItemsIds.includes(item.id)) {
          acc.push(index)
        }
        return acc
      }, []),
    [localItems, checkedItemsIds]
  )

  const handleClick = useCallback(
    (index: number) => {
      const item = localItems[index]
      const exists = checkedItemsIds.includes(item.id)

      const updated = exists
        ? checkedItemsIds.filter((id) => id !== item.id)
        : [...checkedItemsIds, item.id]

      if (options?.sortCheckedItemsIds) {
        updated.sort((a, b) => a.localeCompare(b))
      }

      setCheckedItemsIds(updated)
    },
    [localItems, checkedItemsIds, options?.sortCheckedItemsIds, setCheckedItemsIds]
  )

  return (
    <MUIList
      listItems={localItems.map((item) => ({
        title: item.title,
        description: item.description ?? "",
      }))}
      enableMultiSelect
      listWithDivider
      size={40}
      selected={selectedIndexes}
      handleListItemClick={handleClick}
    />
  )
}
